import React, { useCallback, useMemo, useRef, useState } from "react";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import { INVOICE_ACCOUNT_FILTER_SOURCE_TYPES } from "@common/constants/invoices";
import { noop } from "@common/helpers/function";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { SplitButton } from "@design-system/Button/SplitButton";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useOverlays } from "contexts/OverlayContext";
import {
    getBillcomNewInvoiceUrl,
    getStripeDashboardUrlForFinancialAccount,
    isInvoiceProviderAccount,
} from "@helpers/invoice";
import {
    getBillcomNewBillUrl,
    getRampUrlForInvoiceCreation,
    isBillProviderAccount,
} from "@helpers/bills";
import { CreateStripeConnection } from "@components/stripe/CreateStripeConnection";
import { CreateMercuryConnection } from "@components/mercury/CreateMercuryConnection";
import { CreateBillcomConnection } from "@components/billcom/CreateBillcomConnection";
import { CreateRampConnection } from "@components/ramp/CreateRampConnection";
import { AccountSelectModal } from "@components/invoices/AccountSelectModal";
import { CreateAccountHandle } from "@components/accounts/AddAccounts/lib";
import { FinancialAccount } from "@common/types/financialAccount";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";

const BILL_SOURCE_TYPES = [
    FinancialAccountSourceType.RAMP,
    FinancialAccountSourceType.BILLCOM,
];

interface Props {
    mode: AccrualDocumentImporterMode;
    onUpload: () => void;
    onForwardViaEmail: () => void;
}

function accountUrl(
    account: FinancialAccount,
    mode: AccrualDocumentImporterMode,
): string | undefined {
    if (account.sourceType === FinancialAccountSourceType.BILLCOM) {
        if (mode === "invoices") {
            return getBillcomNewInvoiceUrl();
        }
        return getBillcomNewBillUrl();
    }
    if (mode === "invoices") {
        return getStripeDashboardUrlForFinancialAccount(account);
    }
    return getRampUrlForInvoiceCreation();
}

export const AccrualDraftCreateMenu: React.FC<Props> = ({
    mode,
    onUpload,
    onForwardViaEmail,
}) => {
    const isInvoices = mode === "invoices";
    const entities = useEntitiesContexts();
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { manualAccrualCreation: isManualAccrualCreationEnabled } =
        useTypedFlags();
    const { hasAccess: hasImporterAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const canImport = isManualAccrualCreationEnabled && hasImporterAccess;
    const stripeConnectionRef = useRef<CreateAccountHandle>(null);
    const mercuryConnectionRef = useRef<CreateAccountHandle>(null);
    const billcomConnectionRef = useRef<CreateAccountHandle>(null);
    const rampConnectionRef = useRef<CreateAccountHandle>(null);
    const [accountChoices, setAccountChoices] = useState<
        FinancialAccount[] | null
    >(null);

    const accounts = useMemo(() => {
        const sourceTypes = isInvoices
            ? INVOICE_ACCOUNT_FILTER_SOURCE_TYPES
            : BILL_SOURCE_TYPES;
        return (entities ?? []).flatMap((entity) =>
            entity.financialAccounts.filter((account) =>
                sourceTypes.includes(account.sourceType),
            ),
        );
    }, [entities, isInvoices]);

    const stripeAccounts = useMemo(
        () =>
            accounts.filter(
                (account) =>
                    account.sourceType === FinancialAccountSourceType.STRIPE,
            ),
        [accounts],
    );
    const hasStripeAccounts = stripeAccounts.length > 0;
    const hasMercuryAccounts = accounts.some(
        (account) =>
            account.sourceType === FinancialAccountSourceType.MERCURY &&
            isInvoiceProviderAccount(account),
    );
    const hasBillcomAccounts = accounts.some(
        (account) => account.sourceType === FinancialAccountSourceType.BILLCOM,
    );
    const hasRampAccounts = accounts.some(
        (account) =>
            account.sourceType === FinancialAccountSourceType.RAMP &&
            isBillProviderAccount(account),
    );

    const guard = useCallback(() => refuseIfViewOnly(), [refuseIfViewOnly]);

    const openStripe = useCallback(() => {
        if (guard()) {
            return;
        }
        if (stripeAccounts.length === 0) {
            stripeConnectionRef.current?.initializeConnection();
            return;
        }
        if (stripeAccounts.length === 1) {
            const url = getStripeDashboardUrlForFinancialAccount(
                stripeAccounts[0],
            );
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
            return;
        }
        setAccountChoices(stripeAccounts);
    }, [guard, stripeAccounts]);

    const openMercury = useCallback(() => {
        if (guard()) {
            return;
        }
        if (!hasMercuryAccounts) {
            mercuryConnectionRef.current?.initializeConnection();
            return;
        }
        window.open(
            "https://app.mercury.com/invoicing/create-invoice",
            "_blank",
            "noopener,noreferrer",
        );
    }, [guard, hasMercuryAccounts]);

    const openBillcomInvoice = useCallback(() => {
        if (guard()) {
            return;
        }
        if (!hasBillcomAccounts) {
            billcomConnectionRef.current?.initializeConnection();
            return;
        }
        window.open(getBillcomNewInvoiceUrl(), "_blank", "noopener,noreferrer");
    }, [guard, hasBillcomAccounts]);

    const openKick = useCallback(() => {
        if (guard()) {
            return;
        }
        openOverlay("nativeInvoiceDrawer", {});
    }, [guard, openOverlay]);

    const openRamp = useCallback(() => {
        if (guard()) {
            return;
        }
        if (!hasRampAccounts) {
            rampConnectionRef.current?.initializeConnection();
            return;
        }
        const rampAccounts = accounts.filter(
            (account) =>
                account.sourceType === FinancialAccountSourceType.RAMP ||
                account.sourceType === FinancialAccountSourceType.BILLCOM,
        );
        if (rampAccounts.length > 1) {
            setAccountChoices(rampAccounts);
            return;
        }
        window.open(
            getRampUrlForInvoiceCreation(),
            "_blank",
            "noopener,noreferrer",
        );
    }, [accounts, guard, hasRampAccounts]);

    const openBillcomBill = useCallback(() => {
        if (guard()) {
            return;
        }
        if (!hasBillcomAccounts) {
            billcomConnectionRef.current?.initializeConnection();
            return;
        }
        window.open(getBillcomNewBillUrl(), "_blank", "noopener,noreferrer");
    }, [guard, hasBillcomAccounts]);

    const openImporter = useCallback(() => {
        if (guard()) {
            return;
        }
        openOverlay(
            isInvoices ? "manualInvoiceCreation" : "manualBillCreation",
            {},
        );
    }, [guard, isInvoices, openOverlay]);

    const upload = useCallback(() => {
        if (guard()) {
            return;
        }
        onUpload();
    }, [guard, onUpload]);

    const forward = useCallback(() => {
        if (guard()) {
            return;
        }
        onForwardViaEmail();
    }, [guard, onForwardViaEmail]);

    const onAccountSelect = useCallback(
        (account: FinancialAccount) => {
            setAccountChoices(null);
            const url = accountUrl(account, mode);
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
        },
        [mode],
    );

    return (
        <>
            <SplitButton
                onPrimaryClick={isInvoices ? openKick : openRamp}
                menuAriaLabel={
                    isInvoices ? "More invoice options" : "More bill options"
                }
                menu={
                    isInvoices ? (
                        <>
                            <Menu.Item onClick={openKick}>Kick</Menu.Item>
                            <Menu.Item onClick={openStripe}>Stripe</Menu.Item>
                            <Menu.Item onClick={openMercury}>Mercury</Menu.Item>
                            <Menu.Item onClick={openBillcomInvoice}>
                                BILL
                            </Menu.Item>
                            {canImport && (
                                <Menu.Item onClick={openImporter}>
                                    Import
                                </Menu.Item>
                            )}
                            <Menu.Item onClick={forward}>
                                Forward via email
                            </Menu.Item>
                        </>
                    ) : (
                        <>
                            <Menu.Item onClick={openRamp}>Ramp</Menu.Item>
                            <Menu.Item onClick={openBillcomBill}>
                                BILL
                            </Menu.Item>
                            {canImport && (
                                <Menu.Item onClick={openImporter}>
                                    Importer
                                </Menu.Item>
                            )}
                            <Menu.Item onClick={upload}>Upload bill</Menu.Item>
                            <Menu.Item onClick={forward}>
                                Forward via email
                            </Menu.Item>
                        </>
                    )
                }
            >
                {isInvoices ? "Create invoice" : "Create bill"}
            </SplitButton>
            {!hasStripeAccounts && isInvoices && (
                <CreateStripeConnection
                    ref={stripeConnectionRef}
                    onCreated={noop}
                    toastMessage="Syncing Stripe Invoices... Check back in a few minutes."
                >
                    {() => null}
                </CreateStripeConnection>
            )}
            {!hasMercuryAccounts && isInvoices && (
                <CreateMercuryConnection
                    ref={mercuryConnectionRef}
                    onCreated={noop}
                >
                    {() => null}
                </CreateMercuryConnection>
            )}
            {!hasRampAccounts && !isInvoices && (
                <CreateRampConnection ref={rampConnectionRef} onCreated={noop}>
                    {() => null}
                </CreateRampConnection>
            )}
            {!hasBillcomAccounts && (
                <CreateBillcomConnection
                    ref={billcomConnectionRef}
                    onCreated={noop}
                >
                    {() => null}
                </CreateBillcomConnection>
            )}
            <AccountSelectModal
                onHide={() => setAccountChoices(null)}
                show={accountChoices !== null}
                invoicingAccounts={accountChoices ?? []}
                onSelect={onAccountSelect}
            />
        </>
    );
};
